/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { styled } from "baseui";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";

/* ---------- Container ---------- */

export function TableActionBar({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}): JSX.Element {
  const { colors } = useDexterTheme().currentTheme;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px",
        backgroundColor: colors.headerColor,
        border: `1px solid ${colors.dropdownBorderColor}`,
        borderRadius: "8px",
        flexShrink: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ---------- Generic slot ---------- */
const ItemRoot = styled<"div", { $width?: string; $grow?: boolean }>(
  "div",
  ({ $width, $grow }) => ({
    display: "flex",
    alignItems: "center",
    height: "36px",
    width: $width,
    flexGrow: $grow ? 1 : 0,
    flexShrink: $grow ? 1 : 0,
    minWidth: 0,
    boxSizing: "border-box",
  })
);

export interface ActionBarItemProps {
  /** Any component: a BaseUI Button, Input, Select, your own component... */
  children?: React.ReactNode;
  /** Fixed width */
  width?: string;
  /** Stretch to fill leftover space (an empty grow item acts as a spacer) */
  grow?: boolean;
}

export function ActionBarItem({
  children,
  width,
  grow,
}: ActionBarItemProps): JSX.Element {
  return (
    <ItemRoot $width={width} $grow={grow}>
      {children}
    </ItemRoot>
  );
}

export default TableActionBar;
